import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { styled, alpha } from '@mui/material/styles';
import {
	Badge,
	IconButton,
	Typography,
	ListItem,
	ListItemText,
	ListItemAvatar,
	Avatar,
	Box,
	Tooltip,
	Divider,
	Fade,
	Stack,
	Chip,
	CircularProgress
} from '@mui/material';

// Components
import Iconify from '../../components/Iconify';
import MenuPopover from '../../components/MenuPopover';
import Scrollbar from '../../components/Scrollbar';
import TicketListItem from "../TicketListItem";

// Contexts & Hooks
import { AuthContext } from "../../context/Auth/AuthContext";
import { NotificationSound } from "../../helpers/NotificationSound";
import usePendingTicketsLogic from "../../hooks/usePendingTicketsLogic";
import useResponsive from '../../hooks/useResponsive';
import socket from "../../hooks/useSocket";
import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import PendingTicketsCompactPreview from "../PendingTicketsCompactPreview";

// Styled Components
const NotificationIconButton = styled(IconButton)(({ theme }) => ({
	position: 'relative',
	transition: theme.transitions.create(['background-color', 'transform'], {
		duration: theme.transitions.duration.shorter,
	}),
	'&:hover': {
		backgroundColor: alpha(theme.palette.primary.main, 0.08),
		transform: 'scale(1.05)',
	},
	marginLeft: '0px !important'
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		right: 3,
		top: 3,
		padding: '0 4px',
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText,
		fontWeight: 'bold',
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		'&::after': {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			borderRadius: '50%',
			animation: 'ripple 1.2s infinite ease-in-out',
			border: '1px solid currentColor',
			content: '""',
		},
	},
	'@keyframes ripple': {
		'0%': {
			transform: 'scale(.8)',
			opacity: 1,
		},
		'100%': {
			transform: 'scale(2.4)',
			opacity: 0,
		},
	},
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const NotificationCounter = styled(Chip)(({ theme }) => ({
	height: 24,
	fontSize: '0.75rem',
	fontWeight: 'bold',
	backgroundColor: alpha(theme.palette.primary.main, 0.1),
	color: theme.palette.primary.main,
	border: 'none',
}));

const EmptyNotification = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(4),
	color: theme.palette.text.secondary,
	height: 200,
}));

const NotificationItem = styled(Box)(({ theme }) => ({
	cursor: 'pointer',
	transition: theme.transitions.create(['background-color'], {
		duration: theme.transitions.duration.shorter,
	}),
	'&:hover': {
		backgroundColor: alpha(theme.palette.primary.main, 0.05),
	},
}));

/**
 * Custom hook to manage notifications
 */
function useNotificationSystem(user, queues, profile) {
	const history = useHistory();
	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const [notifications, setNotifications] = useState([]);
	const { tickets, isLoading } = useTickets({ withUnreadMessages: "true" });
	const historyRef = useRef(history);
	const smUp = useResponsive('up', 'sm');
	const { companyId } = user || {};

	// Update ticket ID reference when URL changes
	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	// Initialize volume and desktop notification permissions
	useEffect(() => {
		if (smUp) {
			Notification.requestPermission().catch(err =>
				console.error("Notification permission error:", err)
			);
		}
	}, [smUp]);

	// Filter tickets based on user profile and queues
	useEffect(() => {
		if (!tickets || !queues) return;

		const queueIds = queues.map((q) => q.id);

		let filteredTickets;
		switch (profile) {
			case "user":
				filteredTickets = tickets.filter((t) => queueIds.indexOf(t.queueId) > -1);
				break;
			case "supervisor":
				filteredTickets = tickets.filter((t) => queueIds.indexOf(t.queueId) > -1 || !t.queueId);
				break;
			default:
				filteredTickets = tickets;
				break;
		}

		setNotifications(filteredTickets);
	}, [tickets, queues, profile]);

	// Update document title based on notifications
	useEffect(() => {
		const updateDocumentTitle = () => {
			const activeNotifications = notifications?.filter(n => n?.status !== "closed") || [];

			// Remove existing notification counter from title if exists
			let baseTitle = document.title;
			if (baseTitle.includes(')')) {
				baseTitle = baseTitle.split(")")[1];
			}

			// Add notification counter to title if needed
			if (activeNotifications.length > 0) {
				document.title = `(${activeNotifications.length}) ${baseTitle}`;
			} else {
				document.title = baseTitle;
			}
		};

		updateDocumentTitle();

		// Cleanup function to reset title
		return () => {
			let baseTitle = document.title;
			if (baseTitle.includes(')')) {
				document.title = baseTitle.split(")")[1];
			}
		};
	}, [notifications]);

	// Socket event handlers
	useEffect(() => {
		if (!user || !companyId) return;

		const queueIds = queues?.map((q) => q.id) || [];

		const handleConnect = () => {
			socket.emit("getInternalUnreadMessages", { idFrom: user.id });
			socket.emit("joinNotification");
		};

		const handleTicketEvent = (data) => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						const updatedState = [...prevState];
						updatedState.splice(ticketIndex, 1);
						return updatedState;
					}
					return prevState;
				});
			}
		};

		const handleAppMessage = (data) => {
			if (
				data?.action === "create" &&
				!data?.message?.read &&
				(data?.ticket?.userId === user?.id || !data?.ticket?.userId) &&
				companyId === data?.ticket?.whatsapp?.companyId
			) {
				// Skip user-specific messages for queue they don't belong to
				if (
					profile === 'user' &&
					(queueIds.indexOf(data.ticket.queue?.id) === -1 || data.ticket.queue === null)
				) {
					return;
				}

				// Only process incoming messages (not outgoing)
				if (data?.message?.fromMe === false) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
						if (ticketIndex !== -1) {
							const updatedState = [...prevState];
							updatedState[ticketIndex] = data.ticket;
							return updatedState;
						}
						return [data.ticket, ...prevState];
					});
				}

				// Handle notification display logic
				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current && document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id);

				if (!shouldNotNotificate) {
					handleNotification(data);
				}
			}
		};

		// Register socket event handlers
		socket.on("connect", handleConnect);
		socket.on(`ticket-${companyId}`, handleTicketEvent);
		socket.on(`appMessage-${companyId}`, handleAppMessage);

		// Cleanup function
		return () => {
			socket.off("connect", handleConnect);
			socket.off(`ticket-${companyId}`, handleTicketEvent);
			socket.off(`appMessage-${companyId}`, handleAppMessage);
		};
	}, [user, companyId, profile, queues]);

	// Process and display notifications
	const handleNotification = useCallback((data) => {
		const { message, contact, ticket } = data;

		// Skip outgoing messages
		if (message?.fromMe) return;

		// Process contact cards
		const isContact = message.body?.includes("BEGIN:VCARD");
		let messageBody = message.body;

		if (isContact) {
			let array = messageBody?.split("\n");
			array?.forEach(item => {
				const values = item?.split(":");
				if (values[0] === "FN") {
					messageBody = `${i18n.t("tickets.notification.contactBody")} ${values[1]}`;
				}
			});
		}

		// Create desktop notification
		if (smUp && 'Notification' in window && Notification.permission === 'granted') {
			const options = {
				body: `${messageBody} - ${format(new Date(), "HH:mm")}`,
				icon: contact.profilePicUrl,
				tag: ticket.id,
				renotify: true,
			};

			const notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contact.name}`,
				options
			);

			notification.onclick = e => {
				e.preventDefault();
				window.focus();
				historyRef.current.push(`/tickets/${ticket.uuid}`);
			};
		}


		const globalNotification = localStorage.getItem("globalNotification");
		const userNotification = localStorage.getItem("userNotification");

		if (ticket.userId === user.id) {
			NotificationSound(userNotification, ticket.id)();
		} else {
			if (user.noQueue || ticket.queueId !== null) {
				NotificationSound(globalNotification, ticket.id)();
			}
		}
	}, [smUp, user]);

	// Filter out closed tickets
	const activeNotifications = useMemo(() => {
		return notifications?.filter(notification => notification?.status !== "closed") || [];
	}, [notifications]);

	return {
		notifications: activeNotifications,
		isLoading,
		handleNotificationClick: (ticket) => {
			historyRef.current.push(`/tickets/${ticket.uuid}`);
		}
	};
}

/**
 * NotificationsPopOver Component
 */
const NotificationsPopOver = () => {
	const { user } = useContext(AuthContext);
	const anchorRef = useRef(null);
	const [isOpen, setIsOpen] = useState(false);
	const { queues = [], profile } = user || {};
	const pendingTicketsLogic = usePendingTicketsLogic();

	// Custom hook for notification management
	const {
		notifications,
		isLoading,
		handleNotificationClick
	} = useNotificationSystem(user, queues, profile);

	const pendingTickets = useMemo(
		() => notifications.filter(t => t.status === "pending"),
		[notifications]
	);
	const otherTickets = useMemo(
		() => notifications.filter(t => t.status !== "pending"),
		[notifications]
	);
	const collapsePending = pendingTicketsLogic === "next" && pendingTickets.length > 0;
	const visibleListTickets = collapsePending ? otherTickets : notifications;

	// Menu handlers
	const handleToggle = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	const handleItemClick = (ticket) => {
		handleNotificationClick(ticket);
		handleClose();
	};

	// Display empty state or notification list
	const renderNotificationContent = () => {
		if (isLoading) {
			return (
				<EmptyNotification>
					<CircularProgress size={40} color="primary" sx={{ mb: 2 }} />
					<Typography variant="body2">{i18n.t("notifications.loading")}</Typography>
				</EmptyNotification>
			);
		}

		if (notifications.length === 0) {
			return (
				<EmptyNotification>
					<Iconify
						icon="mdi:bell-off-outline"
						sx={{ width: 40, height: 40, color: 'text.secondary', mb: 1 }}
					/>
					<Typography variant="body2">{i18n.t("notifications.noTickets")}</Typography>
				</EmptyNotification>
			);
		}

		const scrollHeight = Math.min(
			65 * Math.max(visibleListTickets.length, 1) + (collapsePending ? 80 : 0),
			390
		);

		return (
			<Scrollbar sx={{ height: scrollHeight }}>
				{collapsePending && (
					<PendingTicketsCompactPreview onAccepted={handleClose} />
				)}
				{visibleListTickets.map((ticket, index) => (
					<React.Fragment key={ticket.id}>
						<NotificationItem onClick={() => handleItemClick(ticket)}>
							<TicketListItem ticket={ticket} />
						</NotificationItem>
						{index < visibleListTickets.length - 1 && (
							<Divider sx={{ opacity: 0.1 }} />
						)}
					</React.Fragment>
				))}
			</Scrollbar>
		);
	};

	return (
		<>
			<Tooltip title={i18n.t("userType.translate.notifications")} arrow>
				<NotificationIconButton
					ref={anchorRef}
					onClick={handleToggle}
					aria-label={i18n.t("userType.translate.notifications")}
					color="inherit"
				>
					<StyledBadge
						badgeContent={notifications.length}
						max={99}
						color="error"
						invisible={notifications.length === 0}
					>
						<Iconify
							icon="mdi:bell-outline"
							width={24}
							height={24}
							sx={{ color: 'text.primary' }}
						/>
					</StyledBadge>
				</NotificationIconButton>
			</Tooltip>

			<MenuPopover
				open={isOpen}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				sx={{
					mt: 1.5,
					ml: 0.75,
					width: 360,
					'& .MuiMenuItem-root': {
						px: 1.5,
						borderRadius: 0.75,
					},
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
				TransitionComponent={Fade}
			>
				<NotificationHeader>
					<Typography variant="subtitle1" fontWeight="bold">
						{i18n.t("userType.translate.notifications")}
					</Typography>
					<NotificationCounter
						label={notifications.length}
						size="small"
					/>
				</NotificationHeader>

				{renderNotificationContent()}
			</MenuPopover>
		</>
	);
};

export default NotificationsPopOver;